import GrowHorizontalIcon from "@atlaskit/icon/core/grow-horizontal";
import {useMediaQuery} from "@mantine/hooks";
import {forwardRef, useCallback, useEffect, useState, type RefObject, type SyntheticEvent} from "react";
import {ResizableBox, type ResizeCallbackData} from "react-resizable";
import {twJoin} from "tailwind-merge";
import {useAsidePanelContext} from "~/contexts/asidePanelContext";
import {
    ASIDE_PANEL_MIN_WIDTH,
    clampAsidePanelWidth,
    computeAsidePanelMaxWidth,
    persistAsidePanelWidth,
} from "~/utilities/asidePanelSizing";
import "~/components/resizableAsidePanelColumn.css";

const MOBILE_ASIDE_BREAKPOINT = "(max-width: 47.9375em)";

const AsidePanelResizeHandle = forwardRef<
    HTMLDivElement,
    {
        isActive: boolean;
        handleAxis?: string;
    }
>(function AsidePanelResizeHandle({isActive, handleAxis, ...handleProps}, ref) {
    return (
        <div
            {...handleProps}
            ref={ref}
            className={twJoin(
                "react-resizable-handle",
                handleAxis !== undefined && `react-resizable-handle-${handleAxis}`,
                "resizable-aside-panel-column__handle",
                isActive === true && "resizable-aside-panel-column__handle--active",
            )}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panel"
        >
            <span className="resizable-aside-panel-column__handle-icon" aria-hidden="true">
                <GrowHorizontalIcon label="" />
            </span>
        </div>
    );
});

function AsidePanelPortalTarget({
    className,
    registerPortalTarget,
}: {
    className?: string;
    registerPortalTarget: (element: HTMLElement | null) => void;
}) {
    const setPortalTargetRef = useCallback(
        (element: HTMLDivElement | null) => {
            registerPortalTarget(element);
        },
        [registerPortalTarget],
    );

    return (
        <div
            id="aside-panel"
            ref={setPortalTargetRef}
            className={className}
        />
    );
}

export function ResizableAsidePanelColumn({
    layoutRef,
    position = "inline",
    className,
    isVisible = true,
}: {
    layoutRef: RefObject<HTMLElement | null>;
    position?: "inline" | "fixed";
    className?: string;
    /** When false, keeps `#aside-panel` mounted but collapsed (portal target always available). */
    isVisible?: boolean;
}) {
    const {state, setWidth, setIsResizing, registerPortalTarget} = useAsidePanelContext();
    const isMobile = useMediaQuery(MOBILE_ASIDE_BREAKPOINT, false, {getInitialValueInEffect: true});
    const [layoutHeight, setLayoutHeight] = useState<number>(0);
    const [maxWidth, setMaxWidth] = useState<number>(ASIDE_PANEL_MIN_WIDTH);
    const [handleActive, setHandleActive] = useState<boolean>(false);

    const width = state.width;
    const resizeEnabled =
        isVisible === true && isMobile !== true && state.isFullscreen !== true;
    const isDividerActive = handleActive === true || state.isResizing === true;

    const panelClassName = twJoin(
        "resizable-aside-panel-column__panel h-full w-full min-h-0 min-w-0 border-l border-ds-border",
        isDividerActive === true && "resizable-aside-panel-column__panel--active",
    );

    useEffect(() => {
        if (isVisible !== true) {
            return;
        }

        const layoutElement = layoutRef.current;
        if (layoutElement === null) {
            return;
        }

        const updateMeasurements = () => {
            const containerWidth = layoutElement.clientWidth;
            const nextMaxWidth = computeAsidePanelMaxWidth(containerWidth);
            setMaxWidth(nextMaxWidth);
            setLayoutHeight(layoutElement.clientHeight);
            setWidth((currentWidth) => clampAsidePanelWidth(currentWidth, containerWidth));
        };

        updateMeasurements();

        const resizeObserver = new ResizeObserver(updateMeasurements);
        resizeObserver.observe(layoutElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isVisible, layoutRef, setWidth]);

    const handleResize = (_event: SyntheticEvent, data: ResizeCallbackData) => {
        const nextWidth = clampAsidePanelWidth(data.size.width, layoutRef.current?.clientWidth ?? maxWidth * 2);
        setWidth(nextWidth);
    };

    const handleResizeStart = () => {
        setHandleActive(true);
        setIsResizing(true);
    };

    const handleResizeStop = (_event: SyntheticEvent, data: ResizeCallbackData) => {
        setHandleActive(false);
        setIsResizing(false);
        const nextWidth = clampAsidePanelWidth(data.size.width, layoutRef.current?.clientWidth ?? maxWidth * 2);
        setWidth(nextWidth);
        persistAsidePanelWidth(nextWidth);
    };

    const outerClassName = twJoin(
        "resizable-aside-panel-column",
        // Inline/flex layout needs relative for the resize handle; fixed mode must
        // not also get `relative` or it loses viewport docking on Agent Home.
        position === "fixed" ? null : "relative",
        isVisible !== true && "pointer-events-none w-0 min-w-0 max-w-0 overflow-hidden border-0",
        position === "fixed" && isVisible === true && "fixed z-[60] top-[48px] right-0",
        position === "fixed" && isVisible === true && "h-[calc(100dvh-48px)] max-h-[calc(100dvh-48px)]",
        className,
    );

    if (isVisible !== true) {
        return (
            <div className={outerClassName} aria-hidden="true">
                <AsidePanelPortalTarget
                    registerPortalTarget={registerPortalTarget}
                    className="h-0 w-0 overflow-hidden"
                />
            </div>
        );
    }

    if (resizeEnabled !== true) {
        return (
            <div className={outerClassName} style={{width}}>
                <AsidePanelPortalTarget registerPortalTarget={registerPortalTarget} className={panelClassName} />
            </div>
        );
    }

    return (
        <div className={outerClassName}>
            <ResizableBox
                width={width}
                height={layoutHeight > 0 ? layoutHeight : 1}
                axis="x"
                resizeHandles={["w"]}
                minConstraints={[ASIDE_PANEL_MIN_WIDTH, layoutHeight > 0 ? layoutHeight : 1]}
                maxConstraints={[maxWidth, layoutHeight > 0 ? layoutHeight : 1]}
                handleSize={[8, layoutHeight > 0 ? layoutHeight : 1]}
                onResizeStart={handleResizeStart}
                onResize={handleResize}
                onResizeStop={handleResizeStop}
                handle={
                    <AsidePanelResizeHandle
                        isActive={handleActive === true || state.isResizing === true}
                    />
                }
                className="h-full min-h-0"
            >
                <AsidePanelPortalTarget registerPortalTarget={registerPortalTarget} className={panelClassName} />
            </ResizableBox>
        </div>
    );
}

export function AsidePanelFullscreenColumn({
    position = "inline",
    className,
}: {
    position?: "inline" | "fixed";
    className?: string;
}) {
    const {registerPortalTarget} = useAsidePanelContext();

    const outerClassName = twJoin(
        "min-h-0 min-w-0 overflow-hidden bg-ds-elevation-surface-current",
        position === "fixed"
            ? "fixed z-[60] inset-x-0 top-[48px] bottom-0 h-[calc(100dvh-48px)]"
            : "h-full flex-1",
        className,
    );

    return (
        <div className={outerClassName}>
            <AsidePanelPortalTarget
                registerPortalTarget={registerPortalTarget}
                className="h-full w-full min-h-0 min-w-0 overflow-hidden"
            />
        </div>
    );
}
